import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConfigExportImport = ({ buckets, onImport }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 1000));

    const configData = {
      version: '1.0',
      exportDate: new Date()?.toISOString(),
      buckets: buckets?.map(bucket => ({
        ...bucket,
        secretKey: '***REDACTED***'
      }))
    };

    const blob = new Blob([JSON.stringify(configData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `s3-config-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);

    setIsExporting(false);
  };

  const handleImport = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setIsImporting(true);
    const reader = new FileReader();

    reader.onload = async (e) => {
      try {
        const configData = JSON.parse(e?.target?.result);
        await new Promise(resolve => setTimeout(resolve, 1000));
        onImport(configData?.buckets);
      } catch (error) {
        console.error('Failed to import configuration:', error);
      } finally {
        setIsImporting(false);
      }
    };

    reader?.readAsText(file);
    event.target.value = '';
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Download" size={18} />
          Export Configuration
        </h3>
        <div className="bg-card border border-border rounded-lg p-6">
          <p className="text-sm text-muted-foreground mb-4">
            Export your bucket configurations to a JSON file. Secret keys will be redacted for security.
          </p>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 bg-muted rounded-md p-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="FileJson" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">s3-config-{new Date()?.toISOString()?.split('T')?.[0]}.json</span>
              </div>
              <p className="text-xs text-muted-foreground">{buckets?.length} bucket configuration(s)</p>
            </div>
          </div>
          <Button
            iconName="Download"
            iconPosition="left"
            onClick={handleExport}
            loading={isExporting}
            fullWidth
          >
            Export Configuration
          </Button>
        </div>
      </div>
      <div className="border-t border-border pt-6">
        <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Upload" size={18} />
          Import Configuration
        </h3>
        <div className="bg-card border border-border rounded-lg p-6">
          <p className="text-sm text-muted-foreground mb-4">
            Import bucket configurations from a previously exported JSON file. You'll need to re-enter secret keys.
          </p>
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center mb-4">
            <Icon name="FileJson" size={32} className="mx-auto mb-3 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-2">
              Select a configuration file to import
            </p>
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
              id="config-import"
              disabled={isImporting}
            />
            <label htmlFor="config-import">
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                loading={isImporting}
                asChild
              >
                <span>Select File</span>
              </Button>
            </label>
          </div>
          <div className="p-3 bg-warning/10 border border-warning/20 rounded-md">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
              <p className="text-xs text-warning">
                Importing will merge with existing configurations. Duplicate bucket names will be skipped.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-border pt-6">
        <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Info" size={18} />
          Configuration Tips
        </h3>
        <div className="space-y-2">
          <div className="flex items-start gap-3 p-3 bg-muted rounded-md">
            <Icon name="CheckCircle2" size={16} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">Export configurations regularly as backup</p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-muted rounded-md">
            <Icon name="CheckCircle2" size={16} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">Share configurations across team members</p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-muted rounded-md">
            <Icon name="CheckCircle2" size={16} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">Migrate settings between devices easily</p>
          </div>
          <div className="flex items-start gap-3 p-3 bg-muted rounded-md">
            <Icon name="CheckCircle2" size={16} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-sm text-foreground">Keep exported files secure - they contain access keys</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigExportImport;